/**
 * "Delete my account" (App Store 5.1.1(v), Google Play account deletion). Mirrors medopd-backend's
 * accountdeletion/AccountDeletionControllers and the phone app (clinvea-mobile api-client accountDeleteWork.ts):
 *
 * - signed-in staff:   /api/account/deletion          (status, code, schedule, cancel)
 * - signed-in patient: /api/patient-portal/account-deletion
 * - public page:       /api/public/account-deletion   (code → verify → confirm | cancel), no sign-in, for people
 *   without the app — the URL given to Google Play is the web page /delete-account that uses it.
 */
import { fetchApi, fetchPatientApi } from '../client';
import { hashPasswordForTransmission, PASSWORD_HASH_HEADER, PASSWORD_HASH_VALUE } from '../../loginSecurity';

export type AccountDeletionKind = 'LISTING_DOCTOR' | 'LAB_OWNER' | 'PHARMACY_OWNER' | 'PATIENT';
export type AccountDeletionBlocker = 'CLINIC_MEMBER' | 'OWNER_WITH_TEAM' | 'OWNER_OF_CLINIC' | 'PLATFORM_ADMIN';
export type AccountDeletionMethod = 'PASSWORD' | 'CODE';

export interface AccountDeletionStatus {
  audience: 'STAFF' | 'PATIENT';
  kind: AccountDeletionKind | null;
  canDelete: boolean;
  blocker: AccountDeletionBlocker | null;
  graceDays: number;
  methods: AccountDeletionMethod[];
  codeChannel: 'SMS' | 'EMAIL' | null;
  codeHint: string | null;
  upcomingBookings: number;
  hasPublicListing: boolean;
  pending: { requestedAt: string; scheduledFor: string } | null;
  supportEmail: string;
}

export interface AccountDeletionCodeSent {
  channel: 'SMS' | 'EMAIL';
  hint: string | null;
  challengeId: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface AccountDeletionScheduled { status: 'SCHEDULED'; scheduledFor: string; bookingsCancelled: number }
export interface AccountDeletionCancelled { status: 'CANCELLED'; notRestored: number }

export interface AccountDeletionInput {
  method: AccountDeletionMethod;
  password?: string;
  challengeId?: string;
  code?: string;
  confirmation: string;
  reason?: string;
}

export interface PublicDeletionVerified {
  grant: string;
  found: boolean;
  account: AccountDeletionStatus | null;
}

/** The word a person types to confirm; always this English word. */
export const DELETE_WORD = 'DELETE';

export const isDeleteTyped = (value: string) => value.trim().toUpperCase() === DELETE_WORD;

/** Whether the confirm button may be pressed. */
export function deletionReady(f: { method: AccountDeletionMethod; password: string; challengeId: string | null; code: string; typed: string }): boolean {
  if (!isDeleteTyped(f.typed)) return false;
  if (f.method === 'PASSWORD') return f.password.length > 0;
  return !!f.challengeId && /^\d{6}$/.test(f.code.trim());
}

/** The "what happens" lines (keys under auth:accountDeletion.what) for this kind of account. */
export function deletionWhatHappens(kind: AccountDeletionKind | null | undefined, upcomingBookings: number): string[] {
  const lines = ['signIn'];
  if (kind === 'LISTING_DOCTOR' || kind === 'LAB_OWNER' || kind === 'PHARMACY_OWNER') lines.push('listing');
  if ((kind === 'LISTING_DOCTOR' || kind === 'LAB_OWNER') && upcomingBookings > 0) lines.push('bookings');
  if (kind === 'PATIENT') lines.push('family', 'patientRecords');
  else lines.push('records');
  lines.push('grace');
  return lines;
}

const post = (body?: unknown, extra: Record<string, unknown> = {}) =>
  ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body), skipBodyLog: true, ...extra });

export const accountDeletionModule = {
  status: () => fetchApi<AccountDeletionStatus>('/api/account/deletion'),
  sendCode: () => fetchApi<AccountDeletionCodeSent>('/api/account/deletion/code', post()),
  /**
   * The password goes the way login sends it — PBKDF2 under the sign-in email with the format header — or as typed when
   * WebCrypto is unavailable (the server then hashes it itself).
   */
  schedule: async (input: AccountDeletionInput, signInEmail: string) => {
    let body: AccountDeletionInput & { via: 'WEB' } = { ...input, via: 'WEB' };
    let headers: Record<string, string> | undefined;
    if (input.method === 'PASSWORD' && input.password) {
      try {
        body = { ...body, password: await hashPasswordForTransmission(input.password, signInEmail.trim().toLowerCase()) };
        headers = { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE };
      } catch {
        // plaintext; the server hashes it
      }
    }
    return fetchApi<AccountDeletionScheduled>('/api/account/deletion', post(body, headers ? { headers } : {}));
  },
  cancel: () => fetchApi<AccountDeletionCancelled>('/api/account/deletion/cancel', post({ via: 'WEB' })),
};

export const patientAccountDeletionModule = {
  status: () => fetchPatientApi<AccountDeletionStatus>('/api/patient-portal/account-deletion'),
  sendCode: () => fetchPatientApi<AccountDeletionCodeSent>('/api/patient-portal/account-deletion/code', post()),
  schedule: (input: AccountDeletionInput) =>
    fetchPatientApi<AccountDeletionScheduled>('/api/patient-portal/account-deletion', post({ ...input, via: 'WEB' })),
  cancel: () => fetchPatientApi<AccountDeletionCancelled>('/api/patient-portal/account-deletion/cancel', post({ via: 'WEB' })),
};

/** The public page: no sign-in. The emailed code proves the person holds the address; the grant is single-use. */
export const publicAccountDeletionModule = {
  /** {@code language}: the page's language, so the code email is written in it. */
  requestCode: (email: string, language?: string) =>
    fetchApi<{ challengeId: string; expiresInSeconds: number; resendAfterSeconds: number }>(
      '/api/public/account-deletion/code', post({ email, language }, { skipAuth: true })),
  verify: (challengeId: string, email: string, code: string) =>
    fetchApi<PublicDeletionVerified>('/api/public/account-deletion/verify', post({ challengeId, email, code }, { skipAuth: true })),
  confirm: (email: string, grant: string, confirmation: string, reason?: string) =>
    fetchApi<AccountDeletionScheduled>('/api/public/account-deletion/confirm', post({ email, grant, confirmation, reason }, { skipAuth: true })),
  cancel: (email: string, grant: string) =>
    fetchApi<AccountDeletionCancelled>('/api/public/account-deletion/cancel', post({ email, grant }, { skipAuth: true })),
};
